import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, X, ArrowLeft, Sparkles, MapPin, Briefcase, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Discovery = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [shortageAreas, setShortageAreas] = useState([]);
  const [filters, setFilters] = useState({ specialty: '', shortage_area: '' });
  const [aiSuggestions, setAiSuggestions] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const areasRes = await axios.get(`${API}/shortage-areas`);
      setShortageAreas(areasRes.data);

      await fetchProfiles();
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.specialty) params.append('specialty', filters.specialty);
      if (filters.shortage_area) params.append('shortage_area', filters.shortage_area);

      const res = await axios.get(`${API}/discovery?${params.toString()}`, { withCredentials: true });
      setProfiles(res.data);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    }
  };

  const fetchAiSuggestions = async () => {
    try {
      const res = await axios.get(`${API}/ai-suggestions`, { withCredentials: true });
      setAiSuggestions(res.data);
      toast.success('AI suggestions loaded!');
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
      toast.error('Failed to load AI suggestions');
    }
  };

  const handleLike = async () => {
    const current = profiles[currentIndex];
    if (!current) return;

    try {
      const res = await axios.post(`${API}/likes`, { liked_user_id: current.user.id }, { withCredentials: true });
      
      if (res.data.matched) {
        toast.success("It's a match! 💕");
      } else {
        toast.success('Liked!');
      }

      setCurrentIndex(prev => prev + 1);
    } catch (error) {
      console.error('Error liking:', error);
      toast.error('Failed to like');
    }
  };

  const handlePass = () => {
    setCurrentIndex(prev => prev + 1);
  };

  const currentProfile = profiles[currentIndex];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563eb] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-gray-900">Discover</h1>
          <Button
            onClick={fetchAiSuggestions}
            data-testid="ai-suggestions-button"
            className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg"
          >
            <Sparkles className="w-4 h-4" />
            AI Match
          </Button>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex gap-4">
          <Select
            value={filters.specialty || undefined}
            onValueChange={(value) => {
              setFilters({ ...filters, specialty: value === 'all' ? '' : value });
              if (value === 'all') {
                // Auto-apply when clearing filter
                setTimeout(() => fetchProfiles(), 0);
              }
            }}
          >
            <SelectTrigger data-testid="specialty-filter" className="w-[200px]">
              <SelectValue placeholder="All Specialties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Specialties</SelectItem>
              <SelectItem value="Physician">Physician</SelectItem>
              <SelectItem value="Registered Nurse">Registered Nurse</SelectItem>
              <SelectItem value="Nurse Practitioner">Nurse Practitioner</SelectItem>
              <SelectItem value="Physician Assistant">Physician Assistant</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.shortage_area || undefined}
            onValueChange={(value) => {
              setFilters({ ...filters, shortage_area: value === 'all' ? '' : value });
              if (value === 'all') {
                // Auto-apply when clearing filter
                setTimeout(() => fetchProfiles(), 0);
              }
            }}
          >
            <SelectTrigger data-testid="area-filter" className="w-[250px]">
              <SelectValue placeholder="All Areas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Areas</SelectItem>
              {shortageAreas.map((area) => (
                <SelectItem key={area} value={area}>{area}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            onClick={fetchProfiles}
            data-testid="apply-filters-button"
            className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-6"
          >
            Apply
          </Button>
        </div>
      </div>

      {/* AI Suggestions Modal */}
      {aiSuggestions && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setAiSuggestions(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-6 h-6 text-purple-500" />
              <h2 className="text-2xl font-bold text-gray-900">AI Match Suggestions</h2>
            </div>
            <div className="space-y-4 mb-6">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">{aiSuggestions.suggestions}</pre>
            </div>
            <Button
              onClick={() => setAiSuggestions(null)}
              data-testid="close-suggestions-button"
              className="w-full bg-[#2563eb] hover:bg-[#1e40af] text-white py-3 rounded-lg"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {!currentProfile ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <UserIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No more profiles</h2>
            <p className="text-gray-600 mb-6">Check back later for new matches</p>
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-button"
              className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-6 py-3 rounded-lg"
            >
              Back to Dashboard
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Photos */}
            <div className="relative aspect-[4/5] bg-gray-200">
              {currentProfile.profile.photos && currentProfile.profile.photos.length > 0 ? (
                <img
                  src={currentProfile.profile.photos[0]}
                  alt={currentProfile.user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <UserIcon className="w-32 h-32 text-gray-400" />
                </div>
              )}
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              
              {/* Basic info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h2 className="text-3xl font-bold mb-1">{currentProfile.user.name}</h2>
                {currentProfile.profile.age && (
                  <p className="text-lg opacity-90">{currentProfile.profile.age} years old</p>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="p-6 space-y-4">
              {currentProfile.profile.specialty && (
                <div className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-[#2563eb] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">{currentProfile.profile.specialty}</p>
                    {currentProfile.profile.organization && (
                      <p className="text-sm text-gray-600">{currentProfile.profile.organization}</p>
                    )}
                  </div>
                </div>
              )}

              {currentProfile.profile.shortage_area && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#2563eb] flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{currentProfile.profile.shortage_area}</p>
                </div>
              )}

              {currentProfile.profile.bio && (
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">About</h3>
                  <p className="text-gray-700">{currentProfile.profile.bio}</p>
                </div>
              )}

              {currentProfile.profile.interests && currentProfile.profile.interests.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Interests</h3>
                  <div className="flex flex-wrap gap-2">
                    {currentProfile.profile.interests.map((interest, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 text-[#2563eb] rounded-full text-sm font-medium">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 pt-0 flex gap-4">
              <Button
                onClick={handlePass}
                data-testid="pass-button"
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-6 rounded-2xl text-lg font-semibold flex items-center justify-center gap-2"
              >
                <X className="w-6 h-6" />
                Pass
              </Button>
              <Button
                onClick={handleLike}
                data-testid="like-button"
                className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white py-6 rounded-2xl text-lg font-semibold flex items-center justify-center gap-2 shadow-lg"
              >
                <Heart className="w-6 h-6" fill="currentColor" />
                Like
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Discovery;
